import { Header, Sidebar } from '@cockpitai/components/organisms'
import { ChatInput } from '@cockpitai/components/organisms/Chat/components/ChatInput'
import { useTooltipContext } from '@cockpitai/components/organisms/Graphics/components/CustomTooltip'
import { useSideBar } from '@cockpitai/components/organisms/Sidebar/SideBarContext'
import { useChatInputFocus } from '@cockpitai/hooks/useChatInputFocus'
import { useFavoritesData } from '@cockpitai/hooks/useFavoritesData'
import { useIsAtBottom } from '@cockpitai/hooks/useIsAtBottom'
import { useMobileTouchHandler } from '@cockpitai/hooks/useMobileTouchHandler'
import { useMobileViewportAdjustment } from '@cockpitai/hooks/useMobileViewportAdjustment'
import { useWindowResizeHandler } from '@cockpitai/hooks/useWindowResizeHandler'
import { useGetFavorites } from '@cockpitai/integration/favorites/hooks'
import { useLevelNavigation } from '@cockpitai/store/navigation/useLevelNavigation'
import { useSideBarStore } from '@cockpitai/store/navigation/useSideBarStore'
import { isMobile } from '@cockpitai/utils/device'
import { PropsWithChildren } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { twMerge } from 'tailwind-merge'
import { GeneralErrorApplication } from '../GeneralErrorApplication'

export function DashboardTemplate({ children }: PropsWithChildren) {
  const { data } = useGetFavorites()

  const isMobileDevice = isMobile()

  const { isAtBottom } = useIsAtBottom()

  const { isChatInputFocused } = useChatInputFocus(isAtBottom)

  const { navigationHistory, inTransition } = useLevelNavigation()

  const { closeTooltip } = useTooltipContext()

  const { sidebarIsOpen, setSidebarIsOpen } = useSideBar()

  const { toggleIsLocked: setLockSideBarOpened, isLocked: lockSideBarOpened } =
    useSideBarStore()

  useFavoritesData(data ?? [])

  useMobileViewportAdjustment(isMobileDevice)

  useWindowResizeHandler({
    isMobileDevice,
    lockSideBarOpened,
    setLockSideBarOpened,
    setSidebarIsOpen,
  })

  useMobileTouchHandler(isMobileDevice, isAtBottom, isChatInputFocused)

  const openSidebar = () => {
    setSidebarIsOpen(true)

    closeTooltip()
  }

  const closeSidebar = () => {
    setSidebarIsOpen(false)
  }

  const toggleSideBarLocked = () => {
    setLockSideBarOpened()
  }

  return (
    <ErrorBoundary fallbackRender={GeneralErrorApplication}>
      <div
        className={twMerge(
          'relative flex bg-gray-900',
          isMobileDevice && sidebarIsOpen && 'h-screen overflow-hidden',
        )}>
        <main
          id="app-wrapper"
          className={twMerge(
            'lg:max-w-screen-[76.875rem] relative mx-auto w-full max-w-full max-sm:max-w-screen max-sm:overflow-x-hidden',
          )}>
          <div
            data-is-sidebar-open={
              !isMobileDevice
                ? sidebarIsOpen || lockSideBarOpened
                : sidebarIsOpen
            }
            className="grid transition-all duration-500">
            <div>
              <Sidebar
                width={
                  isMobileDevice
                    ? '75%'
                    : sidebarIsOpen || lockSideBarOpened
                      ? '240px'
                      : '44px'
                }
                openSidebar={openSidebar}
                closeSidebar={closeSidebar}
                sidebarIsOpen={sidebarIsOpen || lockSideBarOpened}
                toggleSideBarLocked={toggleSideBarLocked}
                isLockedSideBar={lockSideBarOpened}
              />
            </div>
            <Header />
            <div
              className={twMerge(
                inTransition ? 'page-in-transition' : undefined,
                navigationHistory.length > 0
                  ? 'pt-24 lg:pt-32'
                  : 'pt-20 lg:pt-24',
              )}>
              {children}
            </div>
          </div>
        </main>
        <ChatInput />
      </div>
    </ErrorBoundary>
  )
}
